import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Lock, Loader2, ShieldAlert, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { API, api } from "@/lib/api";

export function LoginModal() {
  const { showLoginModal, setShowLoginModal, login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [forceGoogle, setForceGoogle] = useState(false);
  const [showRecover, setShowRecover] = useState(false);
  const [recoverCode, setRecoverCode] = useState("");
  const [recovering, setRecovering] = useState(false);

  useEffect(() => {
    if (!showLoginModal) return;
    api.get("/auth/login-config").then((r) => setForceGoogle(!!r.data?.force_google)).catch(() => {});
  }, [showLoginModal]);

  const submitRecover = async () => {
    setRecovering(true);
    try {
      await api.post("/auth/recover", { code: recoverCode.trim() });
      toast.success("Recovery accepted — password sign-in re-enabled.");
      setForceGoogle(false);
      setShowRecover(false);
      setRecoverCode("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Invalid or already-used recovery code.");
    } finally {
      setRecovering(false);
    }
  };

  if (!showLoginModal) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await login(email.trim(), password.trim());
      toast.success("Access granted, Doc.");
    } catch (err) {
      setError(err?.response?.data?.detail || "Access denied.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[100] flex items-center justify-center p-6"
        onClick={() => setShowLoginModal(false)}
        data-testid="login-modal-overlay"
      >
        <motion.form
          initial={{ opacity: 0, scale: 0.95, y: 10 }} animate={{ opacity: 1, scale: 1, y: 0 }}
          onClick={(e) => e.stopPropagation()}
          onSubmit={handleSubmit}
          className="glass-panel rounded-2xl p-7 w-full max-w-sm"
          data-testid="login-modal"
        >
          <div className="flex items-center justify-between mb-1">
            <div className="flex items-center gap-2">
              <Lock className="w-3.5 h-3.5 text-cyan-400" />
              <h3 className="font-alien uppercase tracking-[0.2em] text-xs text-cyan-300">Mothership Access</h3>
            </div>
            <button type="button" data-testid="close-login-modal" onClick={() => setShowLoginModal(false)} className="text-slate-500 hover:text-white transition-colors">
              <X className="w-4 h-4" />
            </button>
          </div>
          <p className="text-[11px] text-slate-500 font-mono-data mb-5">Invite-only. Authorized personnel only.</p>

          {!forceGoogle && (
            <>
              <label className="text-[10px] font-mono-data uppercase tracking-wider text-slate-500 block mb-1.5">Email</label>
              <input
                data-testid="login-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="username"
                required
                className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm text-slate-200 outline-none focus:border-cyan-400/40 mb-4 font-mono-data"
              />

              <label className="text-[10px] font-mono-data uppercase tracking-wider text-slate-500 block mb-1.5">Password</label>
              <div className="relative mb-4">
                <input
                  data-testid="login-password-input"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                  required
                  className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 pr-9 text-sm text-slate-200 outline-none focus:border-cyan-400/40 font-mono-data"
                />
                <button
                  type="button"
                  data-testid="toggle-password-visibility"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-500 hover:text-cyan-300 transition-colors"
                  title={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                </button>
              </div>

              {error && (
                <p data-testid="login-error" className="flex items-center gap-1.5 text-[11px] text-rose-400 font-mono-data mb-4">
                  <ShieldAlert className="w-3 h-3" /> {error}
                </p>
              )}

              <button
                type="submit"
                data-testid="login-submit-button"
                disabled={submitting}
                className="w-full bg-cyan-500/15 border border-cyan-400/40 text-cyan-300 hover:bg-cyan-500/25 transition-colors rounded-lg py-2.5 text-sm font-medium flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Dock In"}
              </button>

              <div className="flex items-center gap-3 my-4">
                <div className="flex-1 h-px bg-white/10" />
                <span className="text-[9px] font-mono-data uppercase tracking-widest text-slate-600">or</span>
                <div className="flex-1 h-px bg-white/10" />
              </div>
            </>
          )}
          {forceGoogle && (
            <p data-testid="login-google-only-note" className="text-[11px] text-slate-400 font-mono-data mb-4 leading-relaxed">
              This mothership requires <span className="text-cyan-300">Google sign-in</span> on every launch.
            </p>
          )}

          <button
            type="button"
            data-testid="google-signin-button"
            onClick={() => {
              // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
              // Standalone Google OAuth via the mothership (Doc's own Google client). If a ship
              // bounced Doc here to authenticate (?sso_return=...), resume that exact handshake
              // after sign-in so the ship receives its identity token; otherwise land in the OS.
              const ssoReturn = new URLSearchParams(window.location.search).get("sso_return");
              window.location.href = `${API}/auth/google/start?redirect=${encodeURIComponent(ssoReturn || "/os")}`;
            }}
            className="w-full bg-white/[0.04] border border-white/15 text-slate-200 hover:bg-white/[0.09] transition-colors rounded-lg py-2.5 text-sm font-medium flex items-center justify-center gap-2.5"
          >
            <svg width="15" height="15" viewBox="0 0 24 24" aria-hidden="true">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
              <path fill="#FBBC05" d="M5.84 14.1c-.22-.66-.35-1.36-.35-2.1s.13-1.44.35-2.1V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l3.66-2.84z" />
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z" />
            </svg>
            Sign in with Google
          </button>

          {forceGoogle && (
            <div className="mt-3" data-testid="login-recover-section">
              {!showRecover ? (
                <button type="button" data-testid="login-recover-toggle" onClick={() => setShowRecover(true)}
                  className="w-full text-center text-[10px] font-mono-data text-slate-500 hover:text-amber-300 transition-colors underline decoration-dotted">
                  Locked out? Use a recovery code
                </button>
              ) : (
                <div>
                  <input
                    data-testid="login-recover-input"
                    value={recoverCode}
                    onChange={(e) => setRecoverCode(e.target.value.toUpperCase())}
                    placeholder="AVA9-3F2K-77QX-1MZP"
                    className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm text-slate-200 outline-none focus:border-amber-400/40 mb-2 font-mono-data tracking-widest"
                  />
                  <button type="button" data-testid="login-recover-submit" onClick={submitRecover} disabled={recovering}
                    className="w-full bg-amber-500/15 border border-amber-400/40 text-amber-200 hover:bg-amber-500/25 transition-colors rounded-lg py-2 text-xs font-medium flex items-center justify-center gap-2 disabled:opacity-50">
                    {recovering ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Recover password sign-in"}
                  </button>
                </div>
              )}
            </div>
          )}

          <button
            type="button"
            data-testid="peek-inside-ava-link"
            onClick={() => { window.location.href = "/preview"; }}
            className="w-full text-center text-[10px] font-mono-data uppercase tracking-wider text-slate-500 hover:text-cyan-300 transition-colors mt-4"
          >
            Not ready to sign in? Peek inside AVA →
          </button>
        </motion.form>
      </motion.div>
    </AnimatePresence>
  );
}
